import React from 'react';
import { useOnlineStatus } from '../contexts/OnlineStatusContext';

/**
 * App-wide connectivity indicator.
 *
 * Reads from OnlineStatusContext (which mirrors React Query's onlineManager and
 * mutation cache), so it always agrees with the engine that actually pauses and
 * replays offline writes:
 *  - offline  → reassure the user their changes are saved locally and will sync,
 *  - online with a non-empty queue → show that queued writes are syncing,
 *  - online and empty → render nothing.
 */
export const OfflineBanner: React.FC = () => {
  const { isOnline, pendingMutationCount } = useOnlineStatus();

  if (isOnline && pendingMutationCount === 0) return null;

  const plural = pendingMutationCount === 1 ? '' : 's';
  const message = !isOnline
    ? pendingMutationCount > 0
      ? `Offline — ${pendingMutationCount} change${plural} will sync when you reconnect`
      : "You're offline — changes are saved on this device"
    : `Syncing ${pendingMutationCount} change${plural}…`;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed inset-x-0 bottom-0 z-[9999] flex items-center justify-center gap-2 px-4 py-2 text-center text-sm font-medium text-white ${
        isOnline ? 'bg-amber-600' : 'bg-neutral-800'
      }`}
      style={{ paddingBottom: 'calc(0.5rem + var(--cc-safe-area-bottom, 0px))' }}
    >
      <span
        className={`inline-block h-2 w-2 rounded-full ${
          isOnline ? 'animate-pulse bg-amber-200' : 'bg-red-400'
        }`}
        aria-hidden="true"
      />
      {message}
    </div>
  );
};

export default OfflineBanner;
